import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import {
    FiGitCommit,
    FiCheckCircle,
    FiRotateCcw,
    FiTrendingUp,
    FiFilter,
    FiDownload,
    FiX,
    FiUser,
    FiExternalLink,
    FiInfo
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import {
    ResponsiveContainer,
    RadialBarChart,
    RadialBar,
    PolarAngleAxis,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    LabelList,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    AreaChart,
    Area
} from 'recharts';

const EmployeeGitActivity = ({ isHROrAdmin = false, roleId = null }) => {
    const isAdmin = parseInt(roleId) === 1;
    const view = isAdmin ? 'admin' : 'employee';
    const [heatmapData, setHeatmapData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [dailySummary, setDailySummary] = useState(null);

    // Filters
    // Admin needs monthly by default (to show profiles), Employee uses 'all' for heatmap
    const [selectedMonth, setSelectedMonth] = useState(isAdmin ? new Date().getMonth() + 1 : 'all');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedEmail, setSelectedEmail] = useState(''); // For employee view filtering if admin

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const yearOptions = [2024, 2025, 2026];

    const [dailyActivity, setDailyActivity] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [showDailyModal, setShowDailyModal] = useState(false);
    const [fetchingDaily, setFetchingDaily] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch Heatmap Data (Employee Side)
    const fetchHeatmap = async (email = '') => {
        try {
            setLoading(true);
            const res = await api.get('/gitlab-charts/heatmap', {
                params: {
                    email: email,
                    month: selectedMonth === 'all' ? '' : selectedMonth,
                    year: selectedYear
                }
            });
            if (res.data.status) {
                setHeatmapData(res.data.data);
            }
        } catch (error) {
            console.error("Heatmap fetch error", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDailyActivity = async (date) => {
        try {
            console.log("Fetching activity for:", date, "with email:", selectedEmail);
            setFetchingDaily(true);
            setSelectedDate(date);
            setShowDailyModal(true);
            const res = await api.get('/gitlab-charts/daily-commits', {
                params: { date: date, email: selectedEmail }
            });
            if (res.data.status) {
                setDailyActivity(res.data.data);
                setDailySummary(res.data.summary);
            }
        } catch (error) {
            console.error("Daily activity fetch error", error);
        } finally {
            setFetchingDaily(false);
        }
    };



    useEffect(() => {
        fetchHeatmap(selectedEmail);
    }, [selectedYear, selectedMonth, selectedEmail]);

    // --- Heatmap Helper Logic ---
    const heatmapGrid = useMemo(() => {
        const dataMap = {};
        heatmapData.forEach(item => {
            dataMap[item.date] = parseInt(item.count);
        });

        const months = [];
        const startMonth = selectedMonth === 'all' ? 0 : parseInt(selectedMonth) - 1;
        const endMonth = selectedMonth === 'all' ? 11 : parseInt(selectedMonth) - 1;

        for (let m = startMonth; m <= endMonth; m++) {
            const days = [];
            const year = parseInt(selectedYear);
            const firstDay = new Date(year, m, 1);
            const lastDay = new Date(year, m + 1, 0);

            // Padding for the grid (rows of 7)
            const firstDayOfWeek = firstDay.getDay(); // 0 is Sunday
            // If month view, we might want to start exactly on the 1st

            let d = new Date(firstDay);
            while (d <= lastDay) {
                // Formatting YYYY-MM-DD without UTC shift issues
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                const dateStr = `${yyyy}-${mm}-${dd}`;

                days.push({
                    date: dateStr,
                    count: dataMap[dateStr] || 0,
                    day: d.getDate()
                });
                d.setDate(d.getDate() + 1);
            }

            months.push({ name: monthNames[m].substring(0, 3), days });
        }

        return months;
    }, [heatmapData, selectedMonth, selectedYear]);

    const getColorClass = (count) => {
        if (count === 0) return 'bg-gray-100';
        if (count < 2) return 'bg-emerald-200';
        if (count < 5) return 'bg-emerald-400';
        if (count < 10) return 'bg-emerald-600';
        return 'bg-emerald-800';
    };

    // --- Report Export ---
    const handleDownloadReport = async () => {
        try {
            // Updated to fetch DETAILED COMMITS log for the selected period
            const res = await api.get('/gitlab-charts/daily-commits', {
                params: {
                    month: selectedMonth,
                    year: selectedYear,
                    user_id: selectedEmployee?.user_id || selectedEmployee?.id || 166
                }
            });

            const data = res.data?.data || [];
            if (data.length === 0) {
                toast.error("No commit records found for this period");
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Gitlab Activity Log');

            // --- Header Sections ---
            const companyName = "eNova Software and Hardware Solutions P Ltd";
            const reportTitle = "GitLab Activity Report (Detailed Log)";
            const generatedOn = `Generated on: ${new Date().toLocaleString()}`;
            const duration = `Duration: ${selectedMonth === 'all' ? 'Full Year' : monthNames[selectedMonth - 1]} ${selectedYear}`;

            worksheet.addRow([companyName]);
            worksheet.addRow([reportTitle]);
            worksheet.addRow([generatedOn]);
            worksheet.addRow([duration]);
            worksheet.addRow([]); // Gap row

            // Styling top headers
            for (let i = 1; i <= 4; i++) {
                worksheet.mergeCells(`A${i}:G${i}`);
                const cell = worksheet.getCell(`A${i}`);
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.font = { bold: i === 1, size: i === 1 ? 14 : 11, name: 'Arial' };
            }

            // --- Table Header ---
            const tableHeader = ["Date", "Message", "Project", "Branch", "Prod Ready", "Added", "Deleted"];
            const headerRow = worksheet.addRow(tableHeader);

            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE0E0E0' } // Light Gray
                };
                cell.font = { bold: true, name: 'Arial' };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // --- Data Rows ---
            data.forEach(item => {
                const row = worksheet.addRow([
                    item.commit_datetime?.split(' ')[0] || item.date || 'N/A',
                    item.commit_message || item.message || 'N/A',
                    item.project_name || 'N/A',
                    item.branch_name || item.branch || 'main',
                    item.production_ready || 'No',
                    item.lines_added || 0,
                    item.lines_deleted || 0
                ]);
                row.eachCell((cell) => {
                    cell.alignment = { vertical: 'middle' };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });

            worksheet.columns = [
                { width: 15 }, { width: 50 }, { width: 35 }, { width: 15 }, { width: 12 }, { width: 12 }, { width: 12 }
            ];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Gitlab_Detailed_Report_${selectedMonth}_${selectedYear}.xlsx`;
            link.click();
            toast.success("Professional detailed report downloaded");
        } catch (error) {
            console.error("Export error", error);
            toast.error("Failed to download report");
        }
    };
    const handleDownloadDailyReport = async () => {
        try {
            if (!dailyActivity || dailyActivity.length === 0) {
                toast.error("No daily activity to export");
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Daily Activity');

            // --- Top Headers ---
            worksheet.addRow(["eNova Software and Hardware Solutions P Ltd"]);
            worksheet.addRow(["GitLab Activity Report"]);
            worksheet.addRow([`Generated on: ${new Date().toLocaleString()}`]);
            worksheet.addRow([`Duration: ${selectedDate} to ${selectedDate}`]);
            worksheet.addRow([]); // Gap row

            // Merge and Center Align Top Headers
            for (let i = 1; i <= 4; i++) {
                worksheet.mergeCells(`A${i}:G${i}`);
                const cell = worksheet.getCell(`A${i}`);
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.font = { bold: i === 1, size: i === 1 ? 14 : 11, name: 'Arial' };
            }

            // --- Table Header ---
            const tableHeader = ["Date", "Message", "Project", "Branch", "Prod Ready", "Added", "Deleted"];
            const headerRow = worksheet.addRow(tableHeader);

            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE0E0E0' } // Gray Fill
                };
                cell.font = { bold: true, name: 'Arial' };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // --- Data Rows ---
            dailyActivity.forEach(commit => {
                const row = worksheet.addRow([
                    selectedDate,
                    commit.commit_message || 'N/A',
                    commit.project_name || 'N/A',
                    commit.branch_name || commit.branch || 'main',
                    commit.production_ready || 'No',
                    commit.lines_added || 0,
                    commit.lines_deleted || 0
                ]);
                row.eachCell((cell) => {
                    cell.alignment = { vertical: 'middle' };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });

            worksheet.columns = [
                { width: 15 }, { width: 45 }, { width: 30 }, { width: 15 }, { width: 15 }, { width: 10 }, { width: 10 }
            ];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Gitlab_Daily_Activity_${selectedDate}.xlsx`;
            link.click();
            toast.success("Professional Daily Excel report downloaded");
        } catch (error) {
            console.error("Daily Export error", error);
            toast.error("Failed to download daily report");
        }
    };

    // --- Grid Categorization ---
    const categories = {
        'High_High': { label: 'Star', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
        'High_Moderate': { label: 'High Potential', color: 'bg-blue-50 text-blue-700 border-blue-100' },
        'Moderate_High': { label: 'High Performer', color: 'bg-amber-50 text-amber-700 border-amber-100' },
        'Moderate_Moderate': { label: 'Core Player', color: 'bg-slate-50 text-slate-700 border-slate-100' }
    };

    const getGridBox = (perf, pot) => {
        return `${pot}_${perf}`;
    };

    if (view === 'admin') return null;

    return (
        <div className="p-4 sm:p-6 space-y-6 min-h-screen bg-gray-50/50">
            {/* Header section with view toggle */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2 tracking-tight">
                        My code performance this month
                        <span className="text-emerald-500 bg-emerald-50 text-[10px] font-black px-2 py-0.5 rounded-full border border-emerald-100 uppercase tracking-widest">Live</span>
                    </h1>
                    <p className="text-gray-500 text-sm font-medium mt-1">Track code quality and production readiness</p>
                </div>

                <div className="flex items-center gap-2">
                </div>
            </div>

            {/* Content Area */}
            {view === 'employee' ? (
                /* Heatmap View - ONLY FOR EMPLOYEES */
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white p-4 sm:p-8 rounded-[2rem] shadow-md border border-gray-100 w-full overflow-hidden"
                >
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                        <div>
                            <h2 className="text-xl font-black text-gray-900 tracking-tight">Submission Heatmap</h2>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                Commits Overview • {selectedMonth === 'all' ? 'Full Year' : monthNames[selectedMonth - 1]} {selectedYear}
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            {/* Year Selector */}
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="min-w-[80px] bg-white border border-gray-200 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 p-2.5 outline-none transition-all hover:border-gray-300 cursor-pointer"
                            >
                                {yearOptions.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>

                            {/* Month Selector */}
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                className="min-w-[120px] bg-white border border-gray-200 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 p-2.5 outline-none transition-all hover:border-gray-300 cursor-pointer"
                            >
                                <option value="all">All Months</option>
                                {monthNames.map((name, idx) => (
                                    <option key={name} value={idx + 1}>{name}</option>
                                ))}
                            </select>

                            <div className="flex items-center gap-4 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100 flex-nowrap">
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Less</span>
                                    <div className="flex gap-1.5">
                                        {[0, 1, 5, 10].map(c => (
                                            <div key={c} className={`w-3.5 h-3.5 rounded-md ${getColorClass(c)} shadow-sm`} />
                                        ))}
                                    </div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">More</span>
                                </div>
                                <div className="w-[1px] h-4 bg-gray-200" />
                                <button
                                    onClick={handleDownloadReport}
                                    className="p-1.5 text-gray-400 hover:text-emerald-600 transition-all rounded-lg hover:bg-emerald-100/50"
                                    title="Download Excel Report"
                                >
                                    <FiDownload size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto pb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
                        <div className="flex gap-2 min-w-max w-full">
                            {heatmapGrid.map((month, mIdx) => (
                                <div key={mIdx} className="flex-1 flex flex-col gap-3 min-w-[30px]">
                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest text-center border-b border-gray-50 pb-1">{month.name}</span>
                                    <div className="grid grid-flow-col grid-rows-7 gap-1">
                                        {month.days.map((day) => (
                                            <motion.div
                                                key={day.date}
                                                whileHover={{ scale: 1.25, zIndex: 10, borderRadius: '4px' }}
                                                className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-sm cursor-pointer transition-all duration-200 ${getColorClass(day.count)} border border-white/20`}
                                                title={`${day.date}: ${day.count} production ready commits`}
                                                onClick={() => fetchDailyActivity(day.date)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col sm:flex-row gap-6 sm:items-center pt-6 border-t border-gray-50">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 flex items-center justify-center bg-emerald-50 text-emerald-600 rounded-2xl shadow-inner">
                                <FiCheckCircle size={20} />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Total Prod Ready</p>
                                <p className="text-2xl font-black text-emerald-600 tracking-tight">
                                    {heatmapData.reduce((acc, curr) => acc + parseInt(curr.count), 0)}
                                </p>
                            </div>
                        </div>
                        <div className="flex-1 text-xs text-gray-400 font-medium italic">
                            Commit details are fetched based on your authenticated GitLab email. Only "Production Ready" activity is visualized here.
                        </div>
                    </div>
                </motion.div>
            ) : null}

            {/* Employee Details Popup */}
            <AnimatePresence>
                {showModal && selectedEmployee && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowModal(false)}
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl relative overflow-hidden z-10"
                        >
                            <div className="p-8">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="absolute right-6 top-6 p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
                                >
                                    <FiX size={20} />
                                </button>

                                <div className="flex flex-col items-center text-center">
                                    <div className="relative mb-4">
                                        <div className="w-24 h-24 rounded-full border-4 border-emerald-50 p-1 shadow-lg bg-white overflow-hidden">
                                            <img
                                                src={selectedEmployee.profile_image?.startsWith('http')
                                                    ? selectedEmployee.profile_image
                                                    : `${process.env.REACT_APP_API_URL || ''}/uploads/profiles/${selectedEmployee.profile_image}`}
                                                alt={selectedEmployee.name}
                                                className="w-full h-full rounded-full object-cover"
                                                onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedEmployee.name)}&background=random`; }}
                                            />
                                        </div>
                                        <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white p-1.5 rounded-full border-2 border-white shadow-sm ring-4 ring-white">
                                            <FiTrendingUp size={14} />
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 leading-tight">{selectedEmployee.name}</h3>
                                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">{selectedEmployee.email}</p>

                                    <div className={`mt-4 px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${categories[getGridBox(selectedEmployee.performance, selectedEmployee.potential)].color}`}>
                                        {categories[getGridBox(selectedEmployee.performance, selectedEmployee.potential)].label}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 mt-8">
                                    <MetricCard label="COMMITS" value={selectedEmployee.metrics.commits} icon={<FiGitCommit />} color="blue" />
                                    <MetricCard label="PROD READY" value={selectedEmployee.metrics.production_ready} icon={<FiCheckCircle />} color="emerald" />
                                    <MetricCard label="REVERTS" value={selectedEmployee.metrics.revert} icon={<FiRotateCcw />} color="rose" />
                                    <MetricCard label="EFFICIENCY" value={selectedEmployee.metrics.code_efficient} icon={<FiTrendingUp />} color="indigo" />
                                </div>

                                <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Code Moved</p>
                                        <p className="text-lg font-black text-gray-900">{selectedEmployee.metrics.code_moved.toLocaleString()} <span className="text-xs text-gray-400 font-medium">Lines</span></p>
                                    </div>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="px-6 py-3 bg-gray-900 text-white rounded-2xl text-xs font-black shadow-lg shadow-gray-200 hover:bg-black transition-all active:scale-95"
                                    >
                                        CLOSE
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Daily Heatmap Activity Modal */}
            <AnimatePresence>
                {showDailyModal && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowDailyModal(false)}
                            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h3 className="text-lg font-black text-gray-900 leading-none">Activity on {selectedDate}</h3>
                                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">GitLab Commit History</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleDownloadDailyReport}
                                            className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all"
                                            title="Download Daily Report"
                                        >
                                            <FiDownload size={16} />
                                        </button>
                                        <button onClick={() => { setShowDailyModal(false); setDailySummary(null); }} className="p-1.5 hover:bg-gray-100 rounded-full transition-all text-gray-400">
                                            <FiX size={18} />
                                        </button>
                                    </div>
                                </div>

                                {fetchingDaily ? (
                                    <div className="py-20 flex justify-center">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest animate-pulse">Fetching Git Activity...</p>
                                        </div>
                                    </div>
                                ) : (dailyActivity.length > 0 || dailySummary) ? (
                                    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                                        {/* Summary Row - Matches Image 2 */}
                                        {dailySummary && (
                                            <div className="grid grid-cols-3 gap-2">
                                                <div className="p-2.5 bg-[#f1f7ff] rounded-2xl border border-blue-100 flex flex-col items-center text-center shadow-sm">
                                                    <span className="text-[8px] font-black text-blue-500 uppercase tracking-widest leading-none mb-1">Pushed</span>
                                                    <span className="text-xl font-black text-blue-600">{dailySummary.total_commits}</span>
                                                </div>
                                                <div className="p-2.5 bg-[#f0fff8] rounded-2xl border border-emerald-100 flex flex-col items-center text-center shadow-sm">
                                                    <span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest leading-none mb-1">Prod Ready</span>
                                                    <span className="text-xl font-black text-emerald-600">{dailySummary.production_ready_count}</span>
                                                </div>
                                                <div className="p-2.5 bg-[#fff1f5] rounded-2xl border border-rose-100 flex flex-col items-center text-center shadow-sm">
                                                    <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest leading-none mb-1">Reverted</span>
                                                    <span className="text-xl font-black text-rose-600">{dailySummary.reverted_count}</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Project Name Tags - Matches Image 2 pills */}
                                        {dailySummary?.projects?.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 px-1 pt-2">
                                                {dailySummary.projects.map(p => (
                                                    <span key={p} className="px-3 py-1 bg-[#0d1117] text-white text-[8px] font-black rounded-full shadow-sm hover:bg-black transition-all cursor-default">
                                                        {p}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* Commit Timeline - Matches Image 2 cards */}
                                        <div className="space-y-3">
                                            <h4 className="text-[8px] font-black text-gray-300 uppercase tracking-[0.2em] px-1 mb-2 flex items-center gap-2">
                                                <div className="h-[1px] flex-1 bg-gray-50" />
                                                Commit Timeline
                                                <div className="h-[1px] flex-1 bg-gray-50" />
                                            </h4>

                                            {dailyActivity.map((commit, idx) => (
                                                <motion.div
                                                    key={idx}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.05 }}
                                                    className="p-4 rounded-2xl bg-gray-50/50 border border-gray-100 flex flex-col gap-2 hover:bg-white hover:shadow-lg hover:shadow-gray-200/30 transition-all group overflow-hidden relative"
                                                >
                                                    <div className="flex justify-between items-center relative z-10">
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-xs font-black text-emerald-500 font-mono tracking-tighter bg-emerald-50 px-2 py-1 rounded-lg">
                                                                {commit.commit_id ? commit.commit_id.substring(0, 8) : 'N/A'}
                                                            </span>
                                                            {(commit.production_ready && commit.production_ready.toLowerCase() === 'yes') && (
                                                                <span className="bg-emerald-500 text-white text-[8px] font-black px-2 py-1 rounded-full uppercase tracking-widest shadow-sm">
                                                                    PROD READY
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-gray-400 font-bold">{commit.commit_datetime ? new Date(commit.commit_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                    </div>

                                                    <div className="relative z-10 -mt-1">
                                                        <p className="text-sm font-black text-gray-900 leading-snug tracking-tight group-hover:text-emerald-700 transition-colors">
                                                            {commit.commit_message || 'No commit message provided'}
                                                        </p>
                                                    </div>

                                                    <div className="flex items-center justify-between mt-1 pt-2 border-t border-gray-100/50 relative z-10">
                                                        <div className="flex items-center gap-4">
                                                            <div className="flex items-center gap-1.5 text-sm font-black text-emerald-500">
                                                                <span>+{commit.lines_added || 0}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5 text-sm font-black text-rose-500">
                                                                <span>-{commit.lines_deleted || 0}</span>
                                                            </div>
                                                        </div>
                                                        <div className="text-[10px] text-gray-400 font-black uppercase tracking-widest bg-gray-100 px-3 py-1 rounded-full">
                                                            {commit.branch || 'main'}
                                                        </div>
                                                    </div>

                                                    <div className="absolute right-0 top-0 bottom-0 w-1 bg-emerald-500/0 group-hover:bg-emerald-500 transition-all" />
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="py-12 text-center">
                                        <p className="text-gray-400 text-sm font-medium">No activity found for this day.</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

const GridBox = ({ title, subtitle, type, data, config, onOpenDetails }) => {
    const boxEmployees = data.filter(emp => `${emp.potential}_${emp.performance}` === type);

    return (
        <div className={`relative flex flex-col p-6 rounded-[2rem] border-2 transition-all hover:shadow-lg ${config.color} border-current opacity-90 hover:opacity-100 overflow-hidden`}>
            <div className="relative z-10">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-lg font-black uppercase tracking-tight">{title}</h3>
                        <p className="text-[10px] font-bold opacity-60 uppercase tracking-widest">{subtitle}</p>
                    </div>
                    <div className="p-2 rounded-xl bg-white/50 backdrop-blur-sm border border-white/50">
                        <FiInfo size={14} />
                    </div>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-4 mt-8">
                    {boxEmployees.length > 0 ? (
                        boxEmployees.map(emp => (
                            <motion.button
                                key={emp.id}
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => onOpenDetails(emp)}
                                className="relative flex flex-col items-center gap-1.5 group w-full"
                            >
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-full border-2 border-white shadow-md overflow-hidden ring-2 ring-current ring-offset-1 transition-all group-hover:ring-offset-2 bg-white">
                                        <img
                                            src={emp.profile_image?.startsWith('http')
                                                ? emp.profile_image
                                                : `${process.env.REACT_APP_API_URL || ''}/uploads/profiles/${emp.profile_image}`}
                                            alt={emp.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random`; }}
                                        />
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] bg-white rounded-full flex items-center justify-center text-[9px] font-black shadow-sm group-hover:scale-110 transition-transform text-gray-900 border border-current px-1">
                                        {emp.metrics.production_ready}
                                    </div>
                                </div>
                                <span className="text-[9px] font-bold uppercase text-gray-600 truncate w-full text-center">
                                    {emp.name.split(' ')[0]}
                                </span>
                            </motion.button>
                        ))
                    ) : (
                        <div className="col-span-full flex flex-col items-center justify-center py-12 opacity-30">
                            <FiUser size={32} className="mb-2" />
                            <p className="text-[10px] font-black uppercase tracking-widest text-center">No candidates in this category</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Background decorative text */}
            <div className="absolute -bottom-4 -right-4 text-7xl font-black opacity-5 pointer-events-none select-none uppercase italic">
                {title.split(' ')[0]}
            </div>
        </div>
    );
};

const MetricCard = ({ label, value, icon, color }) => {
    const colors = {
        emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
        blue: 'bg-blue-50 text-blue-600 border-blue-100',
        rose: 'bg-rose-50 text-rose-600 border-rose-100',
        indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100'
    };

    return (
        <div className={`p-4 rounded-3xl border ${colors[color]} flex items-center gap-3 transition-transform hover:scale-[1.02]`}>
            <div className="p-2 bg-white/80 rounded-xl shadow-sm">
                {icon}
            </div>
            <div>
                <p className="text-[9px] font-black uppercase tracking-widest opacity-60 leading-none">{label}</p>
                <p className="text-base font-black mt-1 leading-none">{value}</p>
            </div>
        </div>
    );
};


export default EmployeeGitActivity;
